import {
  freshKdfParams, deriveKek, generateDek, wrapDek, unwrapDek, sealRecord, openRecord,
  generateRecoveryCode, normaliseRecoveryCode, MIN_PASSPHRASE_LENGTH,
  type WrappedDek,
} from './crypto';
import {
  readVaultRoster, writeVaultRecord, writeVaultRecords, removeVaultRecord,
  readVaultKeys, writeClassKeyWrap, replaceAllClassKeyWraps, VaultStaleWriteError,
  VaultWrapRaceError, VaultKeysMovedError,
} from './vaultStore';
import { VaultKeyOpensNothingError } from './vaultErrors';

// Re-exported so a caller handling unlock failures needs one import, as with the store's errors.
export { VaultKeyOpensNothingError };
import {
  unlockClass, lockClass, lockAll, isUnlocked, unlockedDek,
  currentRev, noteRev, noteRecord, resolveRecord, type VaultRecordPlain,
} from './vaultSession';
import {
  cacheClassKey, readCachedClassKey, forgetClassKey, forgetAllClassKeys, requestPersistence, isPersisted,
} from './keyCache';

/**
 * The vault, as the UI sees it. Everything below this line is plumbing; everything above it is a screen.
 *
 * ── THE ONE INVARIANT ───────────────────────────────────────────────────────────────────────────
 *
 * Nothing in this module ever returns an object that could be merged onto a `Student` or a `ClassData`.
 * Callers get strings from `vaultSession`'s resolver, or a `VaultRecordPlain` they are expected to render
 * and discard. `vaultLeakGuard` enforces the rest by value at every write.
 */

export class WeakPassphraseError extends Error {
  constructor() {
    super(`Choose a passphrase of at least ${MIN_PASSPHRASE_LENGTH} characters. It is the only thing `
      + 'standing between an attacker with a copy of the ciphertext and your students\' names, so length '
      + 'matters more here than it does for an ordinary password.');
    this.name = 'WeakPassphraseError';
  }
}

export class VaultLockedError extends Error {
  constructor(classId: string) {
    super(`The vault for class ${classId} is locked. Ask for the passphrase before reading or writing names.`);
    this.name = 'VaultLockedError';
  }
}

export class WrongPassphraseError extends Error {
  constructor() {
    super('That passphrase does not open this vault.');
    this.name = 'WrongPassphraseError';
  }
}

export class NoVaultError extends Error {
  constructor(classId: string) {
    super(`No vault has been set up for class ${classId}.`);
    this.name = 'NoVaultError';
  }
}

export interface SetupResult {
  /**
   * Show this ONCE. It is the only way back in if the passphrase is forgotten.
   *
   * Null when the class already had a vault and this call simply unlocked it — there is no new code to
   * show, and inventing one would mean re-keying, which destroys every existing record.
   */
  recoveryCode: string | null;
  /** True when this call created the vault; false when it opened one that already existed. */
  created: boolean;
  /** Whether the browser agreed to keep the cached key. Reported so the UI can be honest about it. */
  persisted: boolean;
}

/**
 * Create a vault for one class.
 *
 * A fresh random DEK per class, wrapped twice: once under the passphrase, once under a recovery code. The
 * recovery wrap is not optional in the data model even though showing the code to the teacher is —
 * generating it later would require the DEK, which means being unlocked, which is exactly the state a
 * teacher who has forgotten their passphrase is not in.
 */
export async function setupClassVault(
  uid: string,
  classId: string,
  passphrase: string,
): Promise<SetupResult> {
  if (passphrase.trim().length < MIN_PASSPHRASE_LENGTH) throw new WeakPassphraseError();

  const existing = await readVaultKeys(uid);

  /**
   * ONE derivation, reusing the teacher's existing params when they have them, so one passphrase opens
   * every class. Generating fresh params per class is what made unlocking fifteen classes thirty seconds
   * of PBKDF2 and made a cross-class screen impossible to open.
   */
  const passKdf = existing?.kdf ?? freshKdfParams();
  const kek = await deriveKek(passphrase, passKdf);

  /**
   * THE PASSPHRASE MUST OPEN WHAT ALREADY EXISTS.
   *
   * Without this, a second class set up with a typo got wrapped under the typo — and because every wrap
   * shares one KEK, `changePassphrase` would then throw on whichever class mismatched and the passphrase
   * could never be changed again. "One passphrase opens everything" is only true if every setup used the
   * same string, so this is where that is checked. Costs no extra derivation: the KEK is already in hand.
   */
  const anyExistingWrap = existing ? Object.values(existing.classes)[0] : undefined;
  if (anyExistingWrap) {
    try {
      await unwrapDek(anyExistingWrap, kek);
    } catch {
      throw new WrongPassphraseError();
    }
  }

  /**
   * NEVER RE-KEY AN EXISTING VAULT.
   *
   * Generating a fresh DEK here and merging the new wrap over the old one orphans every record already in
   * `vault/roster`: they were sealed under the previous key, both doors to it are gone, and nothing can
   * ever open them. Irrecoverable, and reachable by something as ordinary as a second click on "Set up",
   * or a retry after the roster read below failed once.
   *
   * So an existing vault is UNLOCKED, not replaced. The passphrase has already been verified above, which
   * is what makes this safe rather than merely forgiving.
   */
  /**
   * Adopt a wrap that is already stored: unwrap it, unlock from it, cache it. Shared by the ordinary
   * existing-vault branch and by the loser of a concurrent setup, which must do exactly the same thing.
   */
  const adoptStoredWrap = async (wrap: WrappedDek): Promise<SetupResult> => {
    const dek = await unwrapDek(wrap, kek);
    await unlockClass(uid, classId, dek, (await readVaultRoster(classId)).records);
    await cacheClassKey(uid, classId, dek);
    /**
     * Ask for persistence here too. A new browser opening an existing class through this branch would
     * otherwise never request it, and the cost is a passphrase prompt on every visit until something else
     * happens to ask.
     */
    const persisted = (await isPersisted()) || (await requestPersistence());
    return { recoveryCode: null, created: false, persisted };
  };

  const existingWrap = existing?.classes?.[classId];
  if (existingWrap) return adoptStoredWrap(existingWrap);

  // Extractable, because `wrapKey` requires it. Deliberately not the handle that gets cached — see below.
  const wrappingDek = await generateDek();
  const passWrap = await wrapDek(wrappingDek, kek);

  const recoveryCode = generateRecoveryCode();
  const recKdf = existing?.recoveryKdf ?? freshKdfParams();
  const recWrap = await wrapDek(wrappingDek, await deriveKek(normaliseRecoveryCode(recoveryCode), recKdf));

  /**
   * Read the roster BEFORE writing the wrap.
   *
   * It used to come after, and that lost the recovery code permanently: the wrap is stored, the roster read
   * then fails on a flaky connection, the function throws before returning — and the teacher never sees the
   * code. A retry takes the existing-vault branch and returns `recoveryCode: null`. It cannot be regenerated
   * either, because the session key is non-extractable by then and `wrapDek` cannot wrap it.
   *
   * A roster read for a class with no vault returns an empty record set, so there is nothing to lose by
   * doing it first.
   */
  const existingRecords = (await readVaultRoster(classId)).records;

  /**
   * WRITE ONLY IF NO WRAP EXISTS — and if one appeared while we were working, adopt it.
   *
   * This is the concurrent-setup race, and it is the one failure mode in this feature that destroys data
   * with no error anywhere. Two tabs both read "no vault", both generate a DEK, both write; the merge means
   * the last one wins. The loser then holds a key, a cached key, and a recovery code it has ALREADY SHOWN
   * the teacher, none of which match anything stored — and its verify step still passes, because it checks
   * the records it just sealed against its own session key. Migrate from that tab and every name is gone.
   *
   * `expectAbsent` makes the second write refuse instead. The loser's correct response is not to retry or
   * to surface an error: it is to throw away what it generated and adopt the stored wrap, which is exactly
   * what it would have done had it read the document a second later. The teacher sees a successful setup
   * either way, and `created: false` means the UI does not hand them a recovery code that opens nothing.
   *
   * `expectKdfSalt` covers the other race: a `changePassphrase` on another device rewrites the whole
   * document with fresh params, and merging our older params back over them would lock every OTHER class
   * out of the teacher's own passphrase.
   */
  try {
    await writeClassKeyWrap(uid, classId, {
      kdf: passKdf, wrapped: passWrap, recoveryKdf: recKdf, recoveryWrapped: recWrap,
      expectAbsent: true,
      /**
       * The salt we ACTUALLY DERIVED FROM, not `existing?.kdf?.salt` — which is undefined on a brand-new
       * account and switched the guard off on the branch every new account starts on. See vaultStore.
       */
      expectKdfSalt: passKdf.salt,
    });
  } catch (e) {
    if (!(e instanceof VaultWrapRaceError)) throw e;
    /**
     * Re-read rather than trusting anything in hand. Whatever won the race also decided the account's KDF
     * params, so the KEK may need re-deriving from the stored salt before the wrap will open.
     */
    const now = await readVaultKeys(uid);
    const winner = now?.classes?.[classId];
    /**
     * No winner for THIS class means the refusal was the SALT precondition, not the absent one — a
     * passphrase change landed on another device while we were deriving. Rethrowing the original error
     * was wrong: its message says "Discarding this device's key and adopting the stored one", and
     * nothing was adopted. A retry works, so say that.
     */
    if (!winner) throw new VaultKeysMovedError([]);
    if (now?.kdf && now.kdf.salt !== passKdf.salt) {
      const freshKek = await deriveKek(passphrase, now.kdf);
      const dek = await unwrapDek(winner, freshKek);
      await unlockClass(uid, classId, dek, (await readVaultRoster(classId)).records);
      await cacheClassKey(uid, classId, dek);
      const persisted = (await isPersisted()) || (await requestPersistence());
      return { recoveryCode: null, created: false, persisted };
    }
    return adoptStoredWrap(winner);
  }

  /**
   * Cache a NON-EXTRACTABLE handle, not the one used for wrapping.
   *
   * `generateDek` must be extractable for `wrapKey` to export it. Caching that same handle would put a
   * readable key in IndexedDB on the teacher's primary machine, persistently — any script on the origin
   * could `exportKey('raw', ...)` it — which makes keyCache's promise that the stored key is "unreadable
   * as data, including by us" false on precisely the browser they use every day. Unwrapping the wrap we
   * just wrote yields an equivalent key that cannot be exported.
   */
  const sessionDek = await unwrapDek(passWrap, kek);
  await unlockClass(uid, classId, sessionDek, existingRecords);
  await cacheClassKey(uid, classId, sessionDek);
  const persisted = await requestPersistence();

  return { recoveryCode, created: true, persisted };
}

/** Does this account have a wrap for this class? */
export async function classVaultExists(uid: string, classId: string): Promise<boolean> {
  const keys = await readVaultKeys(uid);
  return !!keys?.classes?.[classId];
}

/**
 * Open a class from the cached key, with no passphrase.
 *
 * The ordinary path on a browser the teacher already used. Returns false when there is no cached key —
 * which is not an error, just the state of a new browser.
 */
export async function unlockFromCache(uid: string, classId: string): Promise<boolean> {
  if (isUnlocked(uid, classId)) return true;
  const dek = await readCachedClassKey(uid, classId);
  if (!dek) return false;
  try {
    const { records } = await readVaultRoster(classId);
    const result = await unlockClass(uid, classId, dek, records);

    /**
     * A STALE KEY LOOKS LIKE SUCCESS, so check for it explicitly.
     *
     * `unlockClass` never throws — it skips records it cannot open so one bad entry does not hide
     * twenty-nine good names. The consequence is that a cached key from a re-keyed vault produces a session
     * that reports "unlocked" with nothing in it, the UI never prompts for the passphrase, and anything
     * saved afterwards is sealed under a key no wrap can reproduce. During a migration that is permanent
     * loss, because the plaintext is deleted on the strength of it.
     */
    if (result.opened === 0 && result.failed.length > 0) {
      lockClass(uid, classId);
      await forgetClassKey(uid, classId);
      return false;
    }
    return true;
  } catch (err) {
    /**
     * NEVER DROP THE KEY HERE. Nothing that can reach this catch proves the key is wrong.
     *
     * This caught everything. `readVaultRoster` is a Firestore read on every class open, so an offline
     * blip or a token refresh at that moment deleted the cached key — and the teacher was asked for
     * their passphrase on the device they had already unlocked, with no explanation, intermittently.
     * The comment described a re-keyed vault; a dropped connection is not that.
     *
     * A stale key is proved by the records failing to decrypt, which is the `opened === 0` check above —
     * not by being unable to fetch them. When we cannot tell, the key stays and the next open tries again.
     *
     * ── AND NOTHING HERE PROVES IT, SO NOTHING HERE DROPS THE KEY ───────────────────────────────
     *
     * This block used to test the error's name against `OperationError`, `WrongPassphraseError`,
     * `VaultKeysCorruptError` and `UnsupportedVaultVersionError` and drop the cached key on a match.
     * Not one of those can arrive here. The try contains exactly two calls: `readVaultRoster`, a plain
     * `getDoc` that throws only Firestore errors, and `unlockClass`, which catches per record and
     * reports `failed` instead of throwing — that is why the `opened === 0` check above has to exist at
     * all. There is no `deriveKek` and no `unwrapDek` on this path; the cache holds the DEK itself.
     *
     * Dead, but not harmless: it read as "crypto failures are handled here", which is the claim that
     * made the `opened === 0` check look redundant to begin with. Deleting it leaves one statement of
     * where a stale key is actually detected.
     */
    console.warn(
      `[unlockFromCache] could not reach the vault for ${classId}; keeping the cached key.`,
      err,
    );
    return false;
  }
}

/** Open a class with the passphrase, or with the recovery code. */
export async function unlockWithSecret(
  uid: string,
  classId: string,
  secret: string,
  kind: 'passphrase' | 'recovery' = 'passphrase',
): Promise<{ opened: number; failed: string[] }> {
  const keys = await readVaultKeys(uid);
  const wrap = kind === 'passphrase' ? keys?.classes?.[classId] : keys?.recovery?.[classId];
  if (!wrap) throw new NoVaultError(classId);

  const kdf = kind === 'passphrase' ? keys!.kdf : keys!.recoveryKdf;
  if (!kdf) throw new NoVaultError(classId);
  const material = kind === 'passphrase' ? secret : normaliseRecoveryCode(secret);

  /**
   * Derive OUTSIDE the try, so a rejected KDF parameter set is not reported as a wrong passphrase.
   *
   * `deriveKek` throws `WeakKdfParamsError` when the stored params are unacceptable — tampered down to
   * `iterations: 1`, say. Catching that alongside the unwrap turned the one failure a teacher cannot have
   * caused into "that passphrase does not open this vault", which blames them and hides the tampering.
   */
  const kek = await deriveKek(material, kdf);

  let dek: CryptoKey;
  try {
    dek = await unwrapDek(wrap, kek);
  } catch {
    // AES-GCM's tag fails rather than yielding a wrong key, so this really is "wrong secret" and not
    // "your data is corrupt" — which is the difference between a retry and a panic.
    throw new WrongPassphraseError();
  }

  const result = await unlockClass(uid, classId, dek, (await readVaultRoster(classId)).records);

  /**
   * A KEY THAT UNWRAPS BUT OPENS NOTHING IS NOT A SUCCESSFUL UNLOCK.
   *
   * `unlockFromCache` has checked this since it was written; this path did not, and it is the path a
   * teacher actually types their passphrase into. The result was that the single worst state this feature
   * can reach — a session holding a key that matches no record in the roster — was reported to the teacher
   * as "Unlocked on this device", with a green banner and every name still showing as a placeholder.
   *
   * That is precisely the aftermath of a concurrent-setup race, so not surfacing it meant the loss stayed
   * invisible to the teacher AND to anyone they asked for help. Worse, the key was then CACHED, so the
   * state persisted across reloads and anything saved afterwards was sealed under it.
   *
   * Thrown rather than returned, because every caller discarded the return value. A caller has to opt
   * into ignoring this, not remember to check for it.
   */
  if (result.opened === 0 && result.failed.length > 0) {
    lockClass(uid, classId);
    await forgetClassKey(uid, classId);
    throw new VaultKeyOpensNothingError(result.failed);
  }

  await cacheClassKey(uid, classId, dek);
  await requestPersistence();
  return result;
}

/**
 * Save one student's PII.
 *
 * Retries ONCE on a stale-revision refusal, after re-reading. Two devices editing the same student is
 * rare but real, and the alternative to a retry is telling the teacher their edit was rejected for a
 * reason they cannot act on.
 */
export async function saveStudentPii(
  uid: string,
  classId: string,
  studentId: string,
  plain: VaultRecordPlain,
): Promise<void> {
  const dek = unlockedDek(uid, classId);
  if (!dek) throw new VaultLockedError(classId);

  const attempt = async (rev: number) => {
    const sealed = await sealRecord(plain, dek, { classId, studentId, rev });
    await writeVaultRecord(classId, studentId, sealed);
    noteRev(uid, classId, studentId, rev);
    noteRecord(uid, classId, studentId, plain);
  };

  try {
    await attempt(currentRev(uid, classId, studentId) + 1);
  } catch (err) {
    if (!(err instanceof VaultStaleWriteError)) throw err;
    const fresh = (await readVaultRoster(classId)).records[studentId];
    await attempt((fresh?.rev ?? 0) + 1);
  }
}

/**
 * MERGE fields into a student's existing record.
 *
 * `saveStudentPii` REPLACES — it seals exactly the object it is given — which is right for an import,
 * where the whole record arrives at once. It is wrong for an edit: a teacher correcting a misspelled
 * surname sends `{lastName}` alone, and replacing would silently delete that child's email, student
 * number, OEN and every guardian detail, with no error and nothing on screen to show it had happened.
 *
 * The merge lives here rather than at the call site because there are several call sites and each one
 * would have to remember. `resolveRecord` reads the decrypted session, so this requires an unlocked
 * vault — which an edit does anyway, since the teacher had to see the old value to change it.
 *
 * A field set to the empty string is kept as an empty string rather than dropped: clearing a guardian
 * phone number is a legitimate edit, and treating it as "no change" would make the field impossible to
 * empty.
 */
export async function updateStudentPii(
  uid: string,
  classId: string,
  studentId: string,
  changes: VaultRecordPlain,
): Promise<void> {
  const dek = unlockedDek(uid, classId);
  if (!dek) throw new VaultLockedError(classId);

  /**
   * MERGE ONTO WHAT THE SERVER HOLDS, re-read on conflict. The session alone is not a safe base.
   *
   * The first version merged onto `resolveRecord` — the in-memory session — and handed the result to
   * `saveStudentPii`, whose stale-write retry re-reads only the REVISION and re-seals the same stale
   * merge one higher. So: laptop and phone both hold revision 3; the laptop saves an email and reaches
   * 4; the phone saves a guardian phone, merges onto its revision-3 copy, is refused as stale, and
   * retries at 5 with the laptop's email silently dropped. The comment claiming two devices were handled
   * was describing the revision check, which protects the WRITE and not the MERGE BASE.
   *
   * So the server record is read and decrypted BEFORE the write, and the caller's changes applied on
   * top of it. Two devices editing different fields of one student therefore both keep their edit.
   *
   * HONEST LIMIT: this closes the window between one device's write and another's READ, not the window
   * between this read and this write — roughly one round trip plus a seal. A device writing inside that
   * window is still overwritten, because `saveStudentPii`'s own stale-write retry re-seals this merge
   * rather than reporting a conflict. Closing it properly needs the read and the write in one
   * transaction, which the store does not currently expose for a single record.
   */
  /**
   * REFUSE rather than replace when a record exists and cannot be opened.
   *
   * `unlockClass` skips records it cannot decrypt, so a stale cached key leaves `resolveRecord` null
   * for a student who demonstrably HAS a record. Merging onto `{}` then wrote just the edited field and
   * replaced everything else — the child's name, email and guardian details deleted by someone adding a
   * phone number. An unreadable record is a reason to stop, not a reason to start a new one.
   */
  /**
   * THE SERVER IS THE MERGE BASE, read before every edit. The session is only a cache.
   *
   * Merging onto the session and relying on a retry does not work, and the reason is worth recording:
   * `saveStudentPii` has its OWN stale-write retry, which re-reads the revision and writes the same
   * plaintext one higher. It therefore SUCCEEDS with the stale merge and never surfaces a conflict for
   * anyone to handle. A catch in this function was dead code.
   *
   * One extra read per edit buys the property that matters: two devices editing different fields of one
   * student both keep their edit, and the only thing that can be lost is the same field written twice.
   */
  /**
   * The fetch and the DECRYPT are separated, because only the second proves anything about the key.
   *
   * One `try` around both reported a transient Firestore failure as VaultKeyOpensNothingError — whose
   * message tells the teacher their passphrase is correct but opens nothing and suggests the recovery
   * code from another device. For an offline blip while saving a phone number, that is wrong on every
   * count and pushes them toward a passphrase reset they do not need.
   */
  const sealed = (await readVaultRoster(classId)).records[studentId];

  let onServer: { plain: VaultRecordPlain; rev: number } | null = null;
  if (sealed) {
    try {
      onServer = {
        plain: await openRecord<VaultRecordPlain>(sealed, dek, { classId, studentId }),
        rev: sealed.rev,
      };
    } catch {
      /**
       * REFUSE rather than replace. `unlockClass` skips records it cannot decrypt, so a stale cached key
       * leaves this student with no readable copy — and merging onto `{}` would write just the edited
       * field and delete their name, email and guardian details. Someone adding a phone number must not
       * be able to erase a record nobody can read.
       */
      throw new VaultKeyOpensNothingError([studentId]);
    }
  }

  /**
   * No stored record at all is the ordinary case for a hand-added student whose details are being
   * entered for the first time, so the session's copy (usually empty) is the right base there.
   */
  const base = onServer?.plain ?? resolveRecord(uid, classId, studentId) ?? {};

  /**
   * Tell the session the revision we actually saw, so the write goes one above the SERVER's rather than
   * one above whatever this browser last wrote.
   *
   * NOT load-bearing — `saveStudentPii`'s own retry reaches the same result — and a mutation round
   * confirmed that removing this line fails no test. It saves a guaranteed-doomed write and retry on
   * every edit from a browser that is behind, which is the common case once two devices are in use.
   */
  if (onServer) noteRev(uid, classId, studentId, onServer.rev);

  await saveStudentPii(uid, classId, studentId, { ...base, ...changes });
}

/** Seal and store a whole roster in one transaction. The import path. */
export async function saveManyStudentPii(
  uid: string,
  classId: string,
  byStudentId: Record<string, VaultRecordPlain>,
): Promise<void> {
  const dek = unlockedDek(uid, classId);
  if (!dek) throw new VaultLockedError(classId);

  const records: Record<string, Awaited<ReturnType<typeof sealRecord>>> = {};
  for (const [studentId, plain] of Object.entries(byStudentId)) {
    records[studentId] = await sealRecord(plain, dek, {
      classId, studentId, rev: currentRev(uid, classId, studentId) + 1,
    });
  }
  await writeVaultRecords(classId, records);
  for (const [studentId, plain] of Object.entries(byStudentId)) {
    noteRev(uid, classId, studentId, records[studentId].rev);
    noteRecord(uid, classId, studentId, plain);
  }
}

/** Forget one student's record entirely — the student was deleted. */
export async function deleteStudentPii(classId: string, studentId: string): Promise<void> {
  await removeVaultRecord(classId, studentId);
}

/**
 * Read the roster back FROM FIRESTORE and decrypt it. The only honest verification.
 *
 * `readStudentPii` reads the in-memory session, which `saveManyStudentPii` populates from the very object
 * being written — so comparing against it compares a plan to a copy of itself. That is what the migration's
 * verify step did, and it meant the deletion of the only plaintext copy was unguarded: a write that
 * committed garbled ciphertext, or one sealed under a key no wrap can reproduce, passed.
 *
 * This re-reads the stored document and opens each record with the session key, so it catches both: a record
 * absent or corrupt in Firestore, and a session key that is not the key the stored wrap yields.
 */
export async function readVaultFromServer(
  uid: string,
  classId: string,
): Promise<{ opened: Record<string, VaultRecordPlain>; failed: string[] }> {
  const dek = unlockedDek(uid, classId);
  if (!dek) throw new VaultLockedError(classId);

  const { records } = await readVaultRoster(classId);
  const opened: Record<string, VaultRecordPlain> = {};
  const failed: string[] = [];
  for (const [studentId, rec] of Object.entries(records)) {
    try {
      opened[studentId] = await openRecord<VaultRecordPlain>(rec, dek, { classId, studentId });
    } catch {
      failed.push(studentId);
    }
  }
  return { opened, failed };
}

/** Read one student's decrypted record for rendering. Null when locked or not vaulted. */
export const readStudentPii = resolveRecord;

/** Lock one class, or everything. Sign-out calls `lockEverything`. */
export async function lockVault(uid: string, classId: string): Promise<void> {
  lockClass(uid, classId);
  await forgetClassKey(uid, classId);
}

export async function lockEverything(): Promise<void> {
  lockAll();
  await forgetAllClassKeys();
}

/**
 * Change the passphrase.
 *
 * Re-wraps every class key in ONE write. This is the payoff of the two-key envelope: with a key derived
 * straight from the passphrase this would re-encrypt every roster the teacher owns, which in practice
 * means nobody ever changes it.
 *
 * Requires the OLD passphrase rather than using unlocked sessions, so that changing it is a deliberate
 * act and an unattended unlocked browser cannot be used to lock the owner out.
 */
export async function changePassphrase(
  uid: string,
  oldPassphrase: string,
  newPassphrase: string,
): Promise<void> {
  if (newPassphrase.trim().length < MIN_PASSPHRASE_LENGTH) throw new WeakPassphraseError();
  const keys = await readVaultKeys(uid);
  if (!keys || !Object.keys(keys.classes).length) throw new NoVaultError('(any)');

  /**
   * TWO derivations total, not two per class. The old KEK unwraps every class key and the new KEK re-wraps
   * them; at 600,000 iterations each, doing this per class would be the difference between a second and a
   * minute for a teacher with fifteen classes.
   */
  const oldKek = await deriveKek(oldPassphrase, keys.kdf);
  const newKdf = freshKdfParams();
  const newKek = await deriveKek(newPassphrase, newKdf);

  const rewrapped: Record<string, WrappedDek> = {};
  for (const [classId, wrap] of Object.entries(keys.classes)) {
    let fresh: CryptoKey;
    try {
      /**
       * `unwrapKey` can only produce a NON-extractable key, which cannot then be re-wrapped. So the bytes
       * are decrypted and re-imported extractable instead. They exist in the clear only inside this loop.
       */
      fresh = await reExportable(wrap, oldKek);
    } catch {
      throw new WrongPassphraseError();
    }
    rewrapped[classId] = await wrapDek(fresh, newKek);
  }

  await replaceAllClassKeyWraps(uid, {
    kdf: newKdf,
    classes: rewrapped,
    ...(keys.recoveryKdf ? { recoveryKdf: keys.recoveryKdf } : {}),
    ...(keys.recovery ? { recovery: keys.recovery } : {}),
    /**
     * What we read, so the write refuses rather than erasing a class protected on another device while
     * these derivations were running. A refusal costs the teacher a retry; the old behaviour cost them
     * every name in that class.
     */
    expectReplacing: Object.keys(keys.classes),
    expectKdfSalt: keys.kdf.salt,
  });
  // Every cached key is now wrapped under a passphrase this browser was not given; drop them so the next
  // open asks once rather than failing repeatedly.
  await forgetAllClassKeys();
  lockAll();
}

/**
 * A class whose recovery code was missing or wrong, so the reset could not proceed.
 */
export class IncompleteRecoveryError extends Error {
  constructor(public readonly classIds: readonly string[]) {
    super(
      `Cannot reset the passphrase: ${classIds.length} class${classIds.length === 1 ? '' : 'es'} could `
      + 'not be opened with the recovery codes provided. Every protected class needs its own code, '
      + 'because resetting changes the key that opens all of them.',
    );
    this.name = 'IncompleteRecoveryError';
  }
}

/**
 * Reset the passphrase using the RECOVERY CODES, for a teacher who has forgotten it.
 *
 * ── THE DEAD END THIS OPENS ─────────────────────────────────────────────────────────────────────
 *
 * `changePassphrase` requires the old passphrase, and `setupClassVault` refuses to create a vault whose
 * passphrase does not open the teacher's existing wraps — both correct, and together they were a trap. A
 * teacher who forgot their passphrase could still open each existing class with that class's recovery
 * code, and could NEVER change the passphrase or protect another class again. Permanently, with nothing
 * to try and nothing for support to do. That is a worse outcome than the forgotten passphrase itself,
 * because it is silent: the Privacy First option simply stops working and never says why.
 *
 * ── WHY IT NEEDS A CODE FOR EVERY CLASS ─────────────────────────────────────────────────────────
 *
 * Because the passphrase KEK is per TEACHER and the recovery codes are per CLASS. Resetting generates one
 * new KEK and re-wraps every class under it, so every class's DEK has to be in hand at that moment. A
 * partial reset is the thing to avoid at all costs: re-wrapping some classes under the new KEK while
 * leaving others under the old one, and then storing the new salt, makes the un-re-wrapped classes
 * permanently unopenable — the passphrase cannot derive their KEK any more and their recovery codes still
 * point at the old one.
 *
 * So this is all-or-nothing. It collects what it can open, and if ANY class is missing it writes nothing
 * and names the classes that blocked it. A teacher who has lost one code keeps everything they had,
 * rather than trading the rest of their classes for the one they could open.
 *
 * `codesByClassId` must cover every class in the keys document. Codes are normalised, so case and
 * dashes do not matter.
 */
export async function resetPassphraseWithRecoveryCodes(
  uid: string,
  codesByClassId: Record<string, string>,
  newPassphrase: string,
): Promise<{ classesRewrapped: number }> {
  if (newPassphrase.trim().length < MIN_PASSPHRASE_LENGTH) throw new WeakPassphraseError();

  const keys = await readVaultKeys(uid);
  if (!keys || !Object.keys(keys.classes).length) throw new NoVaultError('(any)');
  if (!keys.recoveryKdf || !keys.recovery) {
    throw new IncompleteRecoveryError(Object.keys(keys.classes));
  }

  /**
   * ONE derivation per distinct recovery code, plus one for the new passphrase. The recovery KDF params
   * are shared across the account, so each code derives its own KEK from the same salt.
   */
  const dekByClassId: Record<string, CryptoKey> = {};
  const blocked: string[] = [];

  for (const classId of Object.keys(keys.classes)) {
    const code = codesByClassId[classId];
    const recWrap = keys.recovery[classId];
    if (!code || !recWrap) { blocked.push(classId); continue; }
    try {
      const recKek = await deriveKek(normaliseRecoveryCode(code), keys.recoveryKdf);
      dekByClassId[classId] = await reExportable(recWrap, recKek);
    } catch {
      blocked.push(classId);
    }
  }

  // NOTHING is written unless every class can be re-wrapped. See above.
  if (blocked.length) throw new IncompleteRecoveryError(blocked);

  const newKdf = freshKdfParams();
  const newKek = await deriveKek(newPassphrase, newKdf);
  const rewrapped: Record<string, WrappedDek> = {};
  for (const [classId, dek] of Object.entries(dekByClassId)) {
    rewrapped[classId] = await wrapDek(dek, newKek);
  }

  /**
   * The recovery wraps are carried over UNCHANGED, so the codes the teacher just used keep working.
   *
   * Invalidating them here would be the obvious-looking choice and a bad one: the teacher has just
   * demonstrated that the codes are the only thing they reliably have, and a reset is exactly the moment
   * they are most likely to forget the new passphrase too.
   */
  await replaceAllClassKeyWraps(uid, {
    kdf: newKdf,
    classes: rewrapped,
    recoveryKdf: keys.recoveryKdf,
    recovery: keys.recovery,
    // Same reasoning as changePassphrase: refuse rather than erase a concurrently-protected class.
    expectReplacing: Object.keys(keys.classes),
    expectKdfSalt: keys.kdf.salt,
  });

  // Every cached key is now wrapped under a passphrase this browser was not given.
  await forgetAllClassKeys();
  lockAll();

  return { classesRewrapped: Object.keys(rewrapped).length };
}

/**
 * Unwrap a DEK as an EXTRACTABLE key, which `unwrapKey` cannot produce directly.
 *
 * Decrypts the wrapped bytes rather than calling `unwrapKey`, then imports them extractable. Used only by
 * the passphrase change, the one operation that must hand the SAME key to a new wrap. Throws on the wrong
 * KEK, which is how "wrong old passphrase" is detected.
 */
async function reExportable(wrap: WrappedDek, kek: CryptoKey): Promise<CryptoKey> {
  const raw = await crypto.subtle.decrypt(
    {
      name: 'AES-GCM',
      iv: Uint8Array.from(atob(wrap.iv), (c) => c.charCodeAt(0)) as unknown as BufferSource,
    },
    kek,
    Uint8Array.from(atob(wrap.ct), (c) => c.charCodeAt(0)) as unknown as BufferSource,
  );
  return crypto.subtle.importKey('raw', raw, { name: 'AES-GCM', length: 256 }, true, ['encrypt', 'decrypt']);
}

export { isUnlocked, MIN_PASSPHRASE_LENGTH };
export type { VaultRecordPlain };
